import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';
import Styled from 'styled';
import classnames from 'classnames';
import { Formik } from 'formik';
import { Field, FieldProps } from 'formik';
import { Form } from 'formik';
import { fromGlobalId } from 'graphql-relay';
import { withRouter, RouteComponentProps } from 'react-router';
import { Mutation } from '@apollo/client/react/components';
import { NavLink } from 'react-router-dom';
import Helmet from 'helmet/helmet';

import SVGPureIcon from 'lib/images/svg-pure-icon/svg-pure-icon';
import SVGIcon from 'lib/images/svg-icon/svg-icon';
import { AlertContext } from 'lib/alert-context/alert-context';

import addToGroup from 'four-nets/icons/svg/icon_add-to-group.svg';
import { mergeValidators, emptyValidator } from 'four-nets/ui-v2/form/validators';
import Unauthorized from 'four-nets/errors/401/unauthorized';
import { ViewerConsumer } from 'four-nets/user/viewer/viewer';
import LeftMenu from 'four-nets/photoblog/left-menu/left-menu';
import { GroupPrivacy } from 'four-nets/photoblog/groups/group/group-loader.graphql.types';
import Input from 'four-nets/ui-v2/form/input/input';
import TextArea from 'four-nets/ui-v2/form/text-area/text-area';
import RadioInput from 'four-nets/ui-v2/form/radio-input/radio-input';
import UserInput from 'four-nets/photoblog/groups/users-input/users-input';
import { Site } from 'four-nets/site';

import createGroup, { CreateGroupMutation, CreateGroupMutationVariables } from './mutations/create-group.graphql';
import defaultStyles from './create-edit-group.scss';

interface Values {
    name: string;
    description: string;
    privacyType: GroupPrivacy;
}

export interface Props extends RouteComponentProps<Values> {
    styles: typeof defaultStyles;
}

interface State {
    moderators: string[];
    value: string;
}

type Errors = Partial<{ [key in keyof Values]: React.ReactElement<FormattedMessage>[] | true }>;

class CreateGroupError extends Error {
    descriptor: FormattedMessage.MessageDescriptor;

    constructor(message?: string) {
        super(message);
        this.descriptor = defineMessages({
            FAILED_TO_CREATE_GROUP: { id: 'FAILED_TO_CREATE_GROUP' },
        }).FAILED_TO_CREATE_GROUP;
    }
}

class CreateEditGroup extends React.PureComponent<Props, State> {
    static contextTypes = {
        site: PropTypes.instanceOf(Site),
    };

    state: State = { moderators: [], value: '' };

    onChange = (users: string[], value: string) => {
        this.setState({
            moderators: users,
            value,
        });
    };

    renderHelmet = (H: React.ComponentClass) => {
        const { site } = this.context;

        return (
            <FormattedMessage id='GROUPS_CREATE_NEW_GROUP'>
                {newGroup => (
                    <H>
                        <title>
                            {newGroup} | {site.name}
                        </title>
                        ,
                    </H>
                )}
            </FormattedMessage>
        );
    };

    render() {
        const { styles } = this.props;
        return (
            <ViewerConsumer>
                {({ viewer }) =>
                    !viewer ? (
                        <Unauthorized />
                    ) : (
                        <Mutation<CreateGroupMutation, CreateGroupMutationVariables> mutation={createGroup}>
                            {createGroupFn => (
                                <AlertContext.Consumer>
                                    {({ acknowledge }) => (
                                        <Formik<Values>
                                            validate={values => {
                                                const errors: Errors = {};
                                                const nameErrors = mergeValidators([emptyValidator])(values.name);
                                                const descriptionErrors = mergeValidators([emptyValidator])(
                                                    values.description
                                                );
                                                if (nameErrors) {
                                                    errors.name = nameErrors;
                                                }
                                                if (descriptionErrors) {
                                                    errors.description = descriptionErrors;
                                                }
                                                return errors;
                                            }}
                                            initialValues={{
                                                name: '',
                                                description: '',
                                                privacyType: GroupPrivacy.GROUP_PRIVACY_PUBLIC,
                                            }}
                                            onSubmit={(values, actions) => {
                                                actions.setSubmitting(true);
                                                const onAccept = () => {
                                                    const { value } = this.state;
                                                    let { moderators } = this.state;

                                                    const trimmedValue = value.trim();
                                                    if (trimmedValue) {
                                                        moderators = Array.from([...moderators, trimmedValue]);
                                                    }

                                                    createGroupFn({
                                                        variables: {
                                                            input: {
                                                                name: values.name,
                                                                description: values.description,
                                                                privacy: values.privacyType,
                                                                moderators,
                                                            },
                                                        },
                                                    })
                                                        .then(result => {
                                                            const { data } = result || { data: null };
                                                            if (data && data.createGroup && data.createGroup.group) {
                                                                const id = parseInt(
                                                                    fromGlobalId(data.createGroup.group.id)!.id,
                                                                    10
                                                                );
                                                                this.props.history.push(`/group/${id}/detail/`);
                                                            } else {
                                                                const error = new CreateGroupError();
                                                                throw error;
                                                            }
                                                        })
                                                        .catch((err: CreateGroupError) => {
                                                            acknowledge({
                                                                body: err.descriptor || err.message,
                                                                title: <FormattedMessage id='ERROR' />,
                                                            });
                                                        })
                                                        .finally(() => {
                                                            actions.setSubmitting(false);
                                                        });
                                                };
                                                onAccept();
                                            }}
                                        >
                                            {formik => {
                                                const {
                                                    isSubmitting,
                                                    isValid,
                                                    submitForm,
                                                    setFieldValue,
                                                    touched,
                                                    errors,
                                                } = formik;

                                                return (
                                                    <React.Fragment>
                                                        <FormattedMessage id='GROUPS_CREATE_NEW_GROUP'>
                                                            {msg => <h1 className={styles.title}>{msg}</h1>}
                                                        </FormattedMessage>
                                                        <div className={styles.columns}>
                                                            <Helmet render={this.renderHelmet} />
                                                            <LeftMenu style={{ wrapper: styles.wrapper }}>
                                                                <NavLink
                                                                    to={`/new-group/`}
                                                                    className={classnames(
                                                                        styles.navLink,
                                                                        styles.menuOpen
                                                                    )}
                                                                    activeClassName={styles.active}
                                                                    isActive={() => true}
                                                                >
                                                                    <SVGPureIcon
                                                                        src={addToGroup}
                                                                        className={styles.navIcon}
                                                                    />
                                                                    <FormattedMessage id='GROUPS_CREATE_NEW_GROUP' />
                                                                </NavLink>
                                                            </LeftMenu>
                                                            <div className={styles.column}>
                                                                <Form className={styles.form}>
                                                                    <Field name='name'>
                                                                        {({ field }: FieldProps) => (
                                                                            <React.Fragment>
                                                                                <label>
                                                                                    <FormattedMessage id='GROUP_NAME'>
                                                                                        {msg => (
                                                                                            <span
                                                                                                className={styles.label}
                                                                                            >
                                                                                                {msg}
                                                                                            </span>
                                                                                        )}
                                                                                    </FormattedMessage>
                                                                                    <Input
                                                                                        errors={
                                                                                            errors.name
                                                                                                ? [errors.name]
                                                                                                : undefined
                                                                                        }
                                                                                        touched={touched.name}
                                                                                        {...field}
                                                                                    />
                                                                                </label>
                                                                                <FormattedMessage id='GROUPS_CHOOSE_GROUP_NAME_FOR_OTHERS_TO_UNDERSTAND._IT_SHOULD_NOT_BE_TOO_LONG.'>
                                                                                    {msg => (
                                                                                        <span
                                                                                            className={
                                                                                                styles.description
                                                                                            }
                                                                                        >
                                                                                            {msg}
                                                                                        </span>
                                                                                    )}
                                                                                </FormattedMessage>
                                                                            </React.Fragment>
                                                                        )}
                                                                    </Field>
                                                                    <fieldset className={styles.fieldset}>
                                                                        <FormattedMessage id='GROUP_PRIVACY_TYPE'>
                                                                            {msg => (
                                                                                <legend className={styles.label}>
                                                                                    {msg}
                                                                                </legend>
                                                                            )}
                                                                        </FormattedMessage>
                                                                        <Field
                                                                            id={GroupPrivacy.GROUP_PRIVACY_PUBLIC}
                                                                            name='privacyType'
                                                                        >
                                                                            {({ field }: FieldProps) => (
                                                                                <RadioInput
                                                                                    style={{
                                                                                        label: styles.labelRadioInput,
                                                                                    }}
                                                                                    field={field}
                                                                                    id={
                                                                                        GroupPrivacy.GROUP_PRIVACY_PUBLIC
                                                                                    }
                                                                                    label={
                                                                                        <FormattedMessage id='GROUP_PRIVACY_TYPE_PUBLIC_GROUP' />
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </Field>
                                                                        <Field
                                                                            id={GroupPrivacy.GROUP_PRIVACY_PRIVATE}
                                                                            name='privacyType'
                                                                        >
                                                                            {({ field }: FieldProps) => (
                                                                                <RadioInput
                                                                                    field={field}
                                                                                    id={
                                                                                        GroupPrivacy.GROUP_PRIVACY_PRIVATE
                                                                                    }
                                                                                    label={
                                                                                        <FormattedMessage id='GROUP_PRIVACY_TYPE_PRIVATE_GROUP' />
                                                                                    }
                                                                                />
                                                                            )}
                                                                        </Field>
                                                                        <FormattedMessage id='GROUPS_GROUP_PRIVACY_HELPTEXT'>
                                                                            {msg => (
                                                                                <span className={styles.description}>
                                                                                    {msg}
                                                                                </span>
                                                                            )}
                                                                        </FormattedMessage>
                                                                    </fieldset>
                                                                    <Field name='description'>
                                                                        {({ field }: FieldProps) => (
                                                                            <React.Fragment>
                                                                                <label>
                                                                                    <FormattedMessage id='GROUP_DESCRIPTION'>
                                                                                        {msg => (
                                                                                            <span
                                                                                                className={styles.label}
                                                                                            >
                                                                                                {msg}
                                                                                            </span>
                                                                                        )}
                                                                                    </FormattedMessage>
                                                                                    <TextArea
                                                                                        errors={
                                                                                            errors.description
                                                                                                ? [errors.description]
                                                                                                : undefined
                                                                                        }
                                                                                        touched={touched.description}
                                                                                        {...field}
                                                                                        // FIXME: 'field.onChange' handler doesn't work with TextArea
                                                                                        onChange={e =>
                                                                                            setFieldValue(
                                                                                                'description',
                                                                                                e.target.value
                                                                                            )
                                                                                        }
                                                                                    />
                                                                                </label>
                                                                                <FormattedMessage id='GROUPS_ENTER_WHAT_IS_GROUP_ABOUT_AND_WHO_ARE_THEIR_MEMBERS.'>
                                                                                    {msg => (
                                                                                        <span
                                                                                            className={
                                                                                                styles.description
                                                                                            }
                                                                                        >
                                                                                            {msg}
                                                                                        </span>
                                                                                    )}
                                                                                </FormattedMessage>
                                                                            </React.Fragment>
                                                                        )}
                                                                    </Field>
                                                                </Form>
                                                                <div className={styles.form}>
                                                                    <FormattedMessage id='CO_MODERATORS' />
                                                                    <UserInput
                                                                        style={{ inputWrapper: styles.inputWrapper }}
                                                                        onChange={this.onChange}
                                                                        maxLength={5}
                                                                    />
                                                                    <FormattedMessage id='MAXIMUM_NUMBER_OF_MODERATORS'>
                                                                        {msg => (
                                                                            <span
                                                                                className={classnames(
                                                                                    styles.description,
                                                                                    {
                                                                                        [styles.error]:
                                                                                            this.state.moderators
                                                                                                .length === 5 &&
                                                                                            this.state.value,
                                                                                    }
                                                                                )}
                                                                            >
                                                                                {msg}
                                                                            </span>
                                                                        )}
                                                                    </FormattedMessage>
                                                                    <button
                                                                        className={styles.submitButton}
                                                                        onClick={submitForm}
                                                                        disabled={!isValid || isSubmitting}
                                                                    >
                                                                        <SVGIcon src={addToGroup} />
                                                                        <FormattedMessage id='GROUPS_CREATE_NEW_GROUP' />
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </React.Fragment>
                                                );
                                            }}
                                        </Formik>
                                    )}
                                </AlertContext.Consumer>
                            )}
                        </Mutation>
                    )
                }
            </ViewerConsumer>
        );
    }
}

export default Styled(defaultStyles)(withRouter(CreateEditGroup));
